import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { loadLocale } from "@smart/itops-locale-dom";
import { FieldList, Modal, Select } from "@smart/itops-sb-design-system-dom";
import { useProviderInfo } from "@smart/itops-smokeball-components-dom";
import {
  useLoadMatterTypeCategories,
  useUpdateSetting,
} from "@smart/manage-gql-client-dom";

import { GqlSetting } from "../types";

const setupFormSchema = z.object({
  locations: z.array(z.string()).min(1, "Please select one or more locations"),
  areasOfLaw: z
    .array(z.string())
    .min(1, "Please select one or more areas of law"),
});

type SetupFormOptions = z.infer<typeof setupFormSchema>;

export type SetupModalProps = {
  loading?: boolean;
  setting?: GqlSetting;
};

export const SetupModal = ({ loading, setting }: SetupModalProps) => {
  const providerInfo = useProviderInfo();
  const { terms, states } = loadLocale();
  const [updateSetting] = useUpdateSetting();
  const categories = useLoadMatterTypeCategories({
    teamUri: setting?.teamUri,
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<SetupFormOptions>({
    defaultValues: {
      locations: [],
      areasOfLaw: [],
    },
    resolver: zodResolver(setupFormSchema),
  });

  const onSubmit = handleSubmit(async (values) => {
    await updateSetting({
      variables: {
        fields: {
          areasOfLaw: values.areasOfLaw.map((area) => area.trim()),
          locations: values.locations,
        },
      },
    });
  });
  return (
    <form onSubmit={onSubmit}>
      <Modal
        dataTestId="builder-setup-modal"
        open={
          !loading &&
          (!setting?.areasOfLaw?.length || !setting?.locations?.length)
        }
        loading={loading || isSubmitting}
        header={{
          icon: "regularGear",
          text: "Firm Information",
        }}
        footer={{
          buttons: [
            {
              text: "Save Information",
              type: "submit",
            },
          ],
        }}
      >
        <p>
          In order to improve your Intake experience, we need to collect some
          additional information about your firm, including the{" "}
          {terms.state.plural} and areas of law you currently{" "}
          {terms.practise.future} in.
        </p>
        <p>
          You can update these details at any time from your{" "}
          {providerInfo?.label || "Smokeball"} Settings, in the Intake Forms
          section.
        </p>
        <FieldList className="text-paragraph">
          <Controller
            control={control}
            name="locations"
            render={({ field, fieldState }) => (
              <Select
                id={field.name}
                label={terms.state.title}
                options={
                  setting?.locations?.length
                    ? setting.locations.map((l) => ({ value: l, label: l }))
                    : states.map((s) => ({ value: s.name, label: s.name }))
                }
                error={!!fieldState.error}
                message={fieldState.error?.message}
                value={field.value}
                onChange={(v) => field.onChange(v)}
                maxItems={3}
                isMultiple
                mandatory
              />
            )}
          />
          <Controller
            control={control}
            name="areasOfLaw"
            render={({ field, fieldState }) => (
              <Select
                id={field.name}
                label="Areas of law"
                options={
                  categories.loading
                    ? []
                    : (categories.data?.matterTypeCategories || []).map(
                        (cat) => ({ value: cat, label: cat }),
                      )
                }
                error={!!fieldState.error}
                message={fieldState.error?.message}
                value={field.value}
                maxItems={3}
                onChange={(v) => field.onChange(v)}
                isMultiple
                mandatory
                disabled={categories.loading}
              />
            )}
          />
        </FieldList>
      </Modal>
    </form>
  );
};
